import { DEFAULT_REVIEW_SHORTCUTS, type Rating } from "@engo/shared";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AudioPlayer } from "../../components/ui/AudioPlayer.js";
import { Button } from "../../components/ui/button.js";
import { Card } from "../../components/ui/card.js";
import { Input } from "../../components/ui/input.js";
import { ApiError, api } from "../../lib/api.js";

const RATINGS: Rating[] = ["again", "hard", "good", "easy"];

const RATING_LABEL_KEYS: Record<Rating, string> = {
  again: "review.ratingAgain",
  hard: "review.ratingHard",
  good: "review.ratingGood",
  easy: "review.ratingEasy",
};

const RATING_STYLES: Record<Rating, string> = {
  again: "border-rose-400/30 bg-rose-400/10 text-rose-200 hover:bg-rose-400/20",
  hard: "border-amber-400/30 bg-amber-400/10 text-amber-200 hover:bg-amber-400/20",
  good: "border-emerald-400/30 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/20",
  easy: "border-sky-400/30 bg-sky-400/10 text-sky-200 hover:bg-sky-400/20",
};

function normalizeAnswer(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

function buildHint(answer: string): string {
  return answer
    .split(" ")
    .map((word) => (word.length <= 1 ? word : word[0] + "_".repeat(word.length - 1)))
    .join(" ");
}

function displayKey(key: string): string {
  return key === " " ? "Space" : key;
}

export function ReviewPage() {
  const { t } = useTranslation();
  const query = useQuery({
    queryKey: ["flashcards", "due"],
    queryFn: () => api.getDueFlashcards(20),
  });
  const settingsQuery = useQuery({ queryKey: ["settings"], queryFn: () => api.getSettings() });
  const shortcuts = settingsQuery.data?.shortcuts.review ?? DEFAULT_REVIEW_SHORTCUTS;

  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [reviewedCount, setReviewedCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState("");
  const [showHint, setShowHint] = useState(false);
  const answerInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (query.status === "success" && !revealed) {
      answerInputRef.current?.focus();
    }
  }, [query.status, index, revealed]);

  // Debounces duplicate "Enter" keydowns — e.g. an IME finishing composition
  // and immediately re-firing the same Enter — so a single press can never
  // reveal *and* rate in one go.
  const lastEnterAt = useRef(0);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      // IME composition (e.g. Vietnamese Telex/VNI) can emit its own Enter to
      // confirm the composition, immediately followed by the "real" Enter —
      // ignore the composition one so it doesn't count as a keypress here.
      if (event.isComposing) return;

      const target = event.target as HTMLElement | null;
      const isAnswerInput = target === answerInputRef.current;

      if (
        target &&
        !isAnswerInput &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "BUTTON" ||
          target.isContentEditable)
      ) {
        return;
      }
      if (query.status !== "success" || isSubmitting) return;
      const currentCard = query.data.items[index];
      if (!currentCard) return;

      if (event.key === shortcuts.reveal) {
        const now = Date.now();
        if (now - lastEnterAt.current < 200) return;
        lastEnterAt.current = now;

        event.preventDefault();
        if (!revealed) {
          setRevealed(true);
        } else {
          void submitRating("good");
        }
        return;
      }

      // The hint shortcut still fires while the answer box has focus (it's
      // on the user to pick a key here that doesn't appear in typed answers).
      if (event.key === shortcuts.hint && !revealed && currentCard.expected_answer) {
        event.preventDefault();
        setShowHint(true);
        return;
      }

      // Not revealed yet and typing in the answer box: let normal typing through.
      if (isAnswerInput && !revealed) return;

      const keyToRating: Partial<Record<string, Rating>> = {
        [shortcuts.again]: "again",
        [shortcuts.hard]: "hard",
        [shortcuts.good]: "good",
        [shortcuts.easy]: "easy",
      };
      const rating = revealed ? keyToRating[event.key] : undefined;
      if (rating) {
        event.preventDefault();
        void submitRating(rating);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [query.status, query.data, index, revealed, isSubmitting, shortcuts]);

  if (query.status === "pending") {
    return (
      <Card className="mx-auto max-w-xl">
        <p className="text-[var(--text-secondary)]">{t("review.loading")}</p>
      </Card>
    );
  }

  if (query.status === "error") {
    return (
      <Card className="mx-auto max-w-xl">
        <p className="text-sm text-rose-300">
          {query.error instanceof ApiError ? query.error.message : t("review.errorLoad")}
        </p>
      </Card>
    );
  }

  const items = query.data.items;
  const card = items[index];

  async function submitRating(rating: Rating) {
    if (!card || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.reviewFlashcard(card.id, rating);
      setReviewedCount((n) => n + 1);
      setIndex((i) => i + 1);
      setRevealed(false);
      setAnswerText("");
      setShowHint(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("review.errorSubmit"));
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!card) {
    return (
      <div className="mx-auto max-w-xl space-y-6">
        <h1 className="font-display text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
          {t("review.title")}
        </h1>
        <Card className="space-y-2 text-center">
          <p className="font-display text-lg font-semibold text-[var(--text-primary)]">
            {reviewedCount > 0 ? t("review.allDone") : t("review.nothingDue")}
          </p>
          <p className="text-[var(--text-secondary)]">
            {reviewedCount > 0
              ? t("review.reviewedCount", { count: reviewedCount })
              : t("review.newCardsComingSoon")}
          </p>
        </Card>
      </div>
    );
  }

  const progressPct = ((index + 1) / items.length) * 100;
  const hasGrading = revealed && Boolean(card.expected_answer) && answerText.trim().length > 0;
  const isCorrect = hasGrading && normalizeAnswer(answerText) === normalizeAnswer(card.expected_answer!);

  return (
    <div className="mx-auto max-w-xl space-y-5">
      <div>
        <div className="flex items-baseline justify-between gap-3">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-[var(--text-primary)]">
            {t("review.title")}
          </h1>
          <span className="text-xs text-[var(--text-muted)]">
            {index + 1}/{items.length}
          </span>
        </div>
        <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-white/5">
          <div
            className="h-full rounded-full bg-[var(--primary)] transition-[width] duration-300"
            style={{ width: `${progressPct}%` }}
          />
        </div>
        <p className="mt-3 text-sm text-[var(--text-secondary)]">
          {t("review.progress", {
            word: card.entry.word,
            index: index + 1,
            total: items.length,
            due: query.data.due_count,
          })}
        </p>
      </div>

      <Card className="space-y-6">
        {card.entry.audio_url ? <AudioPlayer src={card.entry.audio_url} autoPlay /> : null}
        <div
          className="text-xl text-[var(--text-primary)]"
          dangerouslySetInnerHTML={{ __html: card.front }}
        />

        {card.expected_answer ? (
          <>
            <Input
              ref={answerInputRef}
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              readOnly={revealed}
              placeholder={t("review.answerPlaceholder", {
                reveal: displayKey(shortcuts.reveal),
                hint: displayKey(shortcuts.hint),
              })}
            />
            {!revealed ? (
              showHint ? (
                <p className="font-mono text-sm tracking-wide text-[var(--text-muted)]">
                  {buildHint(card.expected_answer)}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowHint(true)}
                  className="text-xs text-[var(--text-muted)] underline underline-offset-2 hover:text-[var(--text-secondary)]"
                >
                  {t("review.hintButton", { key: displayKey(shortcuts.hint) })}
                </button>
              )
            ) : null}
          </>
        ) : null}

        {hasGrading ? (
          isCorrect ? (
            <p className="text-sm font-medium text-emerald-300">{t("review.answerCorrect")}</p>
          ) : (
            <p className="text-sm font-medium text-rose-300">
              {t("review.answerIncorrect", { answer: card.expected_answer })}
            </p>
          )
        ) : null}

        {revealed ? (
          <>
            <div className="border-t border-[var(--glass-border)] pt-5">
              <div
                className="text-[var(--text-primary)]"
                dangerouslySetInnerHTML={{ __html: card.back }}
              />
            </div>
            {error ? <p className="text-sm text-rose-300">{error}</p> : null}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {RATINGS.map((rating) => (
                <button
                  key={rating}
                  type="button"
                  disabled={isSubmitting}
                  onClick={() => void submitRating(rating)}
                  className={`rounded-xl border px-3 py-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${RATING_STYLES[rating]}`}
                >
                  <span className="block">
                    {t(RATING_LABEL_KEYS[rating])}{" "}
                    <span className="opacity-60">({displayKey(shortcuts[rating])})</span>
                  </span>
                  <span className="block text-xs opacity-70">
                    {card.next_intervals[rating].label}
                  </span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <Button onClick={() => setRevealed(true)} variant="primary" className="w-full">
            {card.expected_answer ? t("review.submitAnswer") : t("review.showAnswer")}
          </Button>
        )}
      </Card>
    </div>
  );
}
